import { z } from "zod";
import { Id } from "../common/ids.js";
import { Locale } from "../common/localized-text.js";
import { CurrencyCode, Money } from "../common/money.js";
import { Timestamp } from "../common/time.js";
import { FilePurpose } from "../files/files.routes.js";
import { MessageKind } from "../messaging/message.routes.js";
import { NOTIFICATION_TYPES } from "../notifications/notifications.routes.js";
import { TENANT_PERMISSIONS } from "../permissions.js";
import { eventSchema } from "./envelope.js";
/** Payload schemas defined so far; each module card adds its own. */
export const EnterpriseVerified = eventSchema("identity.enterprise.verified.v1", z.object({ enterpriseId: Id, buyerEnabled: z.boolean(), sellerEnabled: z.boolean() }));
export const EnterpriseSuspended = eventSchema("identity.enterprise.suspended.v1", z.object({ enterpriseId: Id, reason: z.string() }));
export const EnterpriseReinstated = eventSchema("identity.enterprise.reinstated.v1", z.object({ enterpriseId: Id, reason: z.string() }));
export const MembershipChanged = eventSchema("identity.membership.changed.v1", z.object({
    membershipId: Id,
    enterpriseId: Id,
    userId: Id,
    roles: z.array(z.string()),
    status: z.enum(["invited", "active", "left"]),
    departmentId: Id.nullable().optional(),
    positionId: Id.nullable().optional(),
}));
export const MembershipLeft = eventSchema("identity.membership.left.v1", z.object({
    membershipId: Id,
    enterpriseId: Id,
    userId: Id,
    transferToMembershipId: Id,
    transferToUserId: Id,
}));
export const ContactAccepted = eventSchema("directory.contact.accepted.v1", z.object({
    requestId: Id,
    requester: z.object({ enterpriseId: Id, membershipId: Id, userId: Id }),
    acceptor: z.object({ enterpriseId: Id, membershipId: Id, userId: Id }),
}));
export const RelationshipTransferred = eventSchema("directory.relationship.transferred.v1", z.object({
    enterpriseId: Id,
    from: z.object({ membershipId: Id, userId: Id }),
    to: z.object({ membershipId: Id, userId: Id }),
    contactsMoved: z.number().int().nonnegative(),
    counterpartUserIds: z.array(Id),
}));
export const AuditChainBroken = eventSchema("audit.chain.broken.v1", z.object({
    runId: Id,
    breakCount: z.number().int().positive(),
    breaks: z
        .array(z.object({
        entityType: z.string(),
        entityId: z.string(),
        eventId: Id,
        kind: z.enum(["hash_mismatch", "missing_root", "fork", "unlinked"]),
    }))
        .describe("The first breaks found; the run row in audit.verification_runs has them all"),
}));
/** A person's name or avatar changed; carries their active memberships so cards held of them can be refreshed. */
export const UserChanged = eventSchema("identity.user.changed.v1", z.object({
    userId: Id,
    displayName: z.string(),
    avatarFileId: Id.nullable(),
    memberships: z.array(z.object({ membershipId: Id, enterpriseId: Id })),
}));
export const DeviceRevoked = eventSchema("identity.device.revoked.v1", z.object({ userId: Id, deviceId: Id }));
export const AgreementAccepted = eventSchema("identity.agreement.accepted.v1", z.object({
    enterpriseId: Id,
    type: z.enum(["platform_terms", "buyer_terms", "seller_agreement"]),
    version: z.string(),
    acceptedBy: Id,
}));
const FileEventBase = {
    fileId: Id,
    tenantId: Id.nullable(),
    purpose: FilePurpose,
};
export const FileUploaded = eventSchema("files.file.uploaded.v1", z.object({
    ...FileEventBase,
    objectKey: z.string(),
    sizeBytes: z.number().int().positive(),
    sha256: z.string(),
    contentType: z.string(),
}));
export const FileScanned = eventSchema("files.file.scanned.v1", z.object({ ...FileEventBase, status: z.enum(["clean", "rejected"]), reason: z.string().nullable() }));
/** A module asks for a notification; the worker's notifier resolves recipients and delivers it (NOT-1). */
export const NotificationRequested = eventSchema("notifications.requested.v1", z.object({
    type: z.enum(NOTIFICATION_TYPES),
    recipients: z.discriminatedUnion("kind", [
        z.object({ kind: z.literal("users"), userIds: z.array(Id).min(1) }),
        z.object({ kind: z.literal("enterprise_admins"), enterpriseId: Id }),
        z.object({
            kind: z.literal("permission_holders"),
            enterpriseId: Id,
            permission: z.enum(TENANT_PERMISSIONS),
        }),
    ]),
    enterpriseId: Id.nullable(),
    params: z.record(z.string(), z.string()),
    link: z.string().startsWith("/"),
}));
export const OrderCreated = eventSchema("trade.order.created.v1", z.object({
    orderId: Id,
    buyerTenantId: Id,
    sellerTenantId: Id,
    settlementCurrency: CurrencyCode,
    total: Money,
    fxSnapshotId: Id.nullable(),
    scheduleItems: z.array(z.object({
        itemId: Id,
        type: z.enum(["deposit", "balance", "full"]),
        amount: Money,
        dueTrigger: z.enum(["order_created", "ready_to_ship", "shipped"]),
        releaseTrigger: z.enum(["receipt_confirmed", "shipment_recorded"]),
    })),
}));
/** An order ended before completion (ORD-4, card 054): held items are refunded through Payment. */
export const OrderCancelled = eventSchema("trade.order.cancelled.v1", z.object({
    orderId: Id,
    buyerEnterpriseId: Id,
    sellerEnterpriseId: Id,
    mode: z.enum(["before_payment", "agreed", "payment_window_lapsed"]),
    reason: z.string().nullable(),
    heldItemIds: z.array(Id).describe("Items held in escrow, for Payment to refund"),
    settledBetweenPartiesItemIds: z
        .array(Id)
        .describe("Items paid by bank transfer, whose refund the parties settle themselves (ADR 0005)"),
}));
/** The seller confirmed a bank transfer it received (ADR 0005, card 055); the ledger records it as external (056). */
export const OfflinePaymentConfirmed = eventSchema("trade.offline-payment.confirmed.v1", z.object({
    offlinePaymentId: Id,
    scheduleItemId: Id,
    orderId: Id,
    buyerEnterpriseId: Id,
    sellerEnterpriseId: Id,
    amount: Money,
    paidOn: z.iso.date(),
    bankReference: z.string(),
}));
const ShipmentEvent = { shipmentId: Id, orderId: Id, buyerEnterpriseId: Id, sellerEnterpriseId: Id };
/** The shop recorded a shipment (LOG-1, card 057). */
export const ShipmentRecorded = eventSchema("trade.shipment.recorded.v1", z.object({ ...ShipmentEvent, carrier: z.string(), trackingNumber: z.string() }));
/** A shipment was delivered, by tracking or by proof; `confirmationDueAt` is set once every shipment is (LOG-2). */
export const ShipmentDelivered = eventSchema("trade.shipment.delivered.v1", z.object({
    ...ShipmentEvent,
    deliveredAt: Timestamp,
    source: z.enum(["tracking", "proof"]),
    confirmationDueAt: Timestamp.nullable(),
}));
/** Receipt confirmed (ORD-3, card 058); `scheduleItemIds` are the held items Payment releases (none for a transfer). */
export const ReceiptConfirmed = eventSchema("trade.order.receipt-confirmed.v1", z.object({ orderId: Id, mode: z.enum(["manual", "timeout"]), scheduleItemIds: z.array(Id) }));
/** Both sides agreed a change (ORD-5, card 059): the new totals and schedule; a new order document follows. */
export const ChangeOrderApproved = eventSchema("trade.change-order.approved.v1", z.object({
    changeOrderId: Id,
    orderId: Id,
    buyerEnterpriseId: Id,
    sellerEnterpriseId: Id,
    total: Money,
    topUpItemId: Id.nullable(),
    settledRefund: Money,
    heldRefund: Money,
}));
/** The order is complete (card 058): reviews and invoices follow it in stage 7. */
export const OrderCompleted = eventSchema("trade.order.completed.v1", z.object({
    orderId: Id,
    buyerEnterpriseId: Id,
    sellerEnterpriseId: Id,
    mode: z.enum(["manual", "timeout"]),
    completedAt: Timestamp,
}));
export const ItemHeld = eventSchema("payment.item.held.v1", z.object({ scheduleItemId: Id, orderId: Id, amount: Money, providerRef: z.string() }));
export const ItemReleased = eventSchema("payment.item.released.v1", z.object({ scheduleItemId: Id, orderId: Id, payout: Money, fee: Money, providerRef: z.string() }));
export const ItemRefunded = eventSchema("payment.item.refunded.v1", z.object({ scheduleItemId: Id, orderId: Id, refund: Money, providerRef: z.string() }));
export const ShopOpened = eventSchema("catalog.shop.opened.v1", z.object({ shopId: Id, enterpriseId: Id, slug: z.string() }));
/**
 * The shop's profile or its selling status changed, so what the public sees of it and its products may have: its
 * page, logo, search entries, and images follow.
 */
export const ShopUpdated = eventSchema("catalog.shop.updated.v1", z.object({ shopId: Id, enterpriseId: Id, slug: z.string() }));
/**
 * The shop's bank account for transfers was saved (card 055), with no details. While trade is paid offline the shop
 * can sell from then on, which the worker records (ADR 0021, card 102).
 */
export const ShopBankAccountSet = eventSchema("catalog.shop.bank-account-set.v1", z.object({ shopId: Id, enterpriseId: Id }));
const PublishedProduct = { productId: Id, shopId: Id, enterpriseId: Id, slug: z.string() };
/** A product went live, or back live: public pages and search show it (CAT-4). */
export const ProductPublished = eventSchema("catalog.product.published.v1", z.object({ ...PublishedProduct, locales: z.array(Locale).min(1) }));
/** A live product's published content changed: its revision was applied. */
export const ProductUpdated = eventSchema("catalog.product.updated.v1", z.object({ ...PublishedProduct, locales: z.array(Locale).min(1) }));
/** The seller took a live product off shelf. */
export const ProductUnlisted = eventSchema("catalog.product.unlisted.v1", z.object(PublishedProduct));
/** Operators blocked a product: pages and search drop it, and trade closes its open inquiries (CAT-4). */
export const ProductBlocked = eventSchema("catalog.product.blocked.v1", z.object({ ...PublishedProduct, reason: z.string() }));
/**
 * Operators added a risk-list entry: the worker checks the published products against it and records each match,
 * changing no product (card 075). The entry's terms are in its check, which outlives the entry.
 */
export const RiskEntryAdded = eventSchema("catalog.risk-entry.added.v1", z.object({ entryId: Id }));
/** A person asked for their personal data: the worker builds the archive (IM-8, card 083). */
export const DataExportRequested = eventSchema("identity.data-export.requested.v1", z.object({ exportId: Id, userId: Id }));
const PayoutAccount = {
    accountId: Id,
    shopId: Id,
    enterpriseId: Id,
    provider: z.enum(["fake", "airwallex"]),
};
/** The payout account passed KYB: the shop may sell (ENT-4, ADR 0017). */
export const KybApproved = eventSchema("payment.connected-account.kyb-approved.v1", z.object(PayoutAccount));
/** The payout account was rejected or suspended: the shop may not sell. */
export const KybRevoked = eventSchema("payment.connected-account.kyb-revoked.v1", z.object({ ...PayoutAccount, status: z.enum(["rejected", "suspended"]) }));
const Inquiry = { inquiryId: Id, shopId: Id, buyerEnterpriseId: Id, sellerEnterpriseId: Id };
/** A buyer inquired (INQ-1, card 052); its deal conversation and card follow. */
export const InquiryCreated = eventSchema("trade.inquiry.created.v1", z.object({ ...Inquiry, conversationId: Id, lineCount: z.number().int().positive() }));
export const InquiryDeclined = eventSchema("trade.inquiry.declined.v1", z.object({ ...Inquiry, reason: z.string() }));
export const InquiryWithdrawn = eventSchema("trade.inquiry.withdrawn.v1", z.object(Inquiry));
export const InquiryExpired = eventSchema("trade.inquiry.expired.v1", z.object(Inquiry));
const QuoteEvent = {
    quoteId: Id,
    inquiryId: Id,
    versionNo: z.number().int().positive(),
    buyerEnterpriseId: Id,
    sellerEnterpriseId: Id,
};
/** The shop issued a quote version (INQ-2, card 053); the previous one is superseded. */
export const QuoteIssued = eventSchema("trade.quote.issued.v1", z.object({ ...QuoteEvent, total: Money, validUntil: Timestamp }));
export const QuoteCountered = eventSchema("trade.quote.countered.v1", z.object(QuoteEvent));
export const QuoteAccepted = eventSchema("trade.quote.accepted.v1", z.object({ ...QuoteEvent, orderId: Id }));
export const QuoteExpired = eventSchema("trade.quote.expired.v1", z.object(QuoteEvent));
/** A schedule item became due: its payment window runs until `dueAt` (card 053 for the first item, 054 after). */
export const ScheduleItemDue = eventSchema("trade.schedule-item.due.v1", z.object({
    scheduleItemId: Id,
    orderId: Id,
    buyerEnterpriseId: Id,
    sellerEnterpriseId: Id,
    seq: z.number().int().positive(),
    amount: Money,
    dueAt: Timestamp,
}));
const CaseParties = {
    caseId: Id,
    orderId: Id,
    buyerEnterpriseId: Id,
    sellerEnterpriseId: Id,
};
/** The buyer opened an after-sales case; the order's confirmation stops while it is open (AFS-1, card 087). */
export const AftersaleCaseOpened = eventSchema("aftersales.case.opened.v1", z.object({
    ...CaseParties,
    type: z.enum(["refund", "return", "replacement", "partial_refund"]),
    requestedMinor: z.string().nullable(),
    currency: z.string().length(3),
    responseDueAt: Timestamp,
}));
/**
 * The parties agreed what to do (card 088): what was asked or the seller's counter. On an order paid by bank transfer
 * the seller pays any refund itself; on an escrow order Payment will execute the amount (stage 6).
 */
export const AftersaleCaseAgreed = eventSchema("aftersales.case.agreed.v1", z.object({
    ...CaseParties,
    type: z.enum(["refund", "return", "replacement", "partial_refund"]),
    agreedMinor: z.string().nullable(),
    currency: z.string().length(3),
    settlement: z.enum(["between_parties", "platform"]),
}));
/** A case went to the platform: the seller did not answer in time, or a party escalated it (cards 088, 089). */
export const AftersaleCaseEscalated = eventSchema("aftersales.case.escalated.v1", z.object({ ...CaseParties, cause: z.enum(["seller_silent", "buyer", "seller"]), evidenceDueAt: Timestamp }));
/**
 * A resolver decided a dispute (AFS-3, card 089): grant, partial, or reject, with the amount, and whether it is an
 * appeal's final decision. Payment will execute it on escrow orders (stage 6); on bank-transfer orders the seller pays.
 */
export const AftersaleCaseResolved = eventSchema("aftersales.case.resolved.v1", z.object({
    ...CaseParties,
    outcome: z.enum(["grant", "partial", "reject"]),
    amountMinor: z.string().nullable(),
    currency: z.string().length(3),
    appeal: z.boolean(),
    appealDueAt: Timestamp,
}));
/** Messages a party picked from the order's conversation, kept on the case with their checksum (AFS-3, card 089). */
export const EvidenceExported = eventSchema("messaging.evidence.exported.v1", z.object({ caseId: Id, evidenceId: Id, sha256: z.string(), messageCount: z.number().int().positive() }));
/**
 * An entry was added to a conversation's log (ADR 0029, card 142): its number, kind, and sender, for the consumers
 * that act on new messages. Never its text, which stays in the log.
 */
export const MessageEntryAppended = eventSchema("messaging.entry.appended.v1", z.object({
    conversationId: Id,
    seq: z.number().int().positive(),
    entryId: Id,
    kind: MessageKind,
    senderUserId: Id.nullable(),
}));
/**
 * A case ended; the order's confirmation resumes with the time it had left (cards 087, 088): withdrawn by the buyer,
 * lapsed when the buyer left a counter or rejection unanswered, or settled once the refund or replacement arrived.
 */
export const AftersaleCaseClosed = eventSchema("aftersales.case.closed.v1", z.object({ ...CaseParties, outcome: z.enum(["withdrawn", "lapsed", "settled", "rejected"]) }));
const ReviewPages = {
    reviewId: Id,
    orderId: Id,
    sellerEnterpriseId: Id,
    shopId: Id,
    productIds: z.array(Id),
};
/** A buyer reviewed a completed order (REV-1, card 092); the public pages of the shop and its products revalidate. */
export const ReviewPublished = eventSchema("trade.review.published.v1", z.object(ReviewPages));
/** The seller replied to a review or edited its reply (card 092); the public pages revalidate. */
export const ReviewReplied = eventSchema("trade.review.replied.v1", z.object(ReviewPages));
/** An operator hid a review (card 092); the public pages drop it at once. */
export const ReviewHidden = eventSchema("trade.review.hidden.v1", z.object(ReviewPages));
/** A seller issued an invoice or credit note (INV-2, card 093); the worker renders its PDF. */
export const InvoiceIssued = eventSchema("trade.invoice.issued.v1", z.object({
    invoiceId: Id,
    orderId: Id,
    type: z.enum(["invoice", "credit_note"]),
    number: z.string(),
    buyerEnterpriseId: Id,
    sellerEnterpriseId: Id,
}));
export const EVENT_SCHEMAS = {
    "identity.enterprise.verified.v1": EnterpriseVerified,
    "identity.enterprise.suspended.v1": EnterpriseSuspended,
    "identity.enterprise.reinstated.v1": EnterpriseReinstated,
    "identity.membership.changed.v1": MembershipChanged,
    "identity.membership.left.v1": MembershipLeft,
    "identity.agreement.accepted.v1": AgreementAccepted,
    "identity.user.changed.v1": UserChanged,
    "identity.device.revoked.v1": DeviceRevoked,
    "directory.contact.accepted.v1": ContactAccepted,
    "directory.relationship.transferred.v1": RelationshipTransferred,
    "audit.chain.broken.v1": AuditChainBroken,
    "files.file.uploaded.v1": FileUploaded,
    "files.file.scanned.v1": FileScanned,
    "notifications.requested.v1": NotificationRequested,
    "catalog.shop.opened.v1": ShopOpened,
    "catalog.shop.updated.v1": ShopUpdated,
    "catalog.shop.bank-account-set.v1": ShopBankAccountSet,
    "catalog.product.published.v1": ProductPublished,
    "catalog.product.updated.v1": ProductUpdated,
    "catalog.product.unlisted.v1": ProductUnlisted,
    "catalog.product.blocked.v1": ProductBlocked,
    "catalog.risk-entry.added.v1": RiskEntryAdded,
    "identity.data-export.requested.v1": DataExportRequested,
    "trade.review.published.v1": ReviewPublished,
    "trade.review.replied.v1": ReviewReplied,
    "trade.review.hidden.v1": ReviewHidden,
    "trade.invoice.issued.v1": InvoiceIssued,
    "aftersales.case.opened.v1": AftersaleCaseOpened,
    "aftersales.case.agreed.v1": AftersaleCaseAgreed,
    "aftersales.case.escalated.v1": AftersaleCaseEscalated,
    "aftersales.case.resolved.v1": AftersaleCaseResolved,
    "messaging.evidence.exported.v1": EvidenceExported,
    "messaging.entry.appended.v1": MessageEntryAppended,
    "aftersales.case.closed.v1": AftersaleCaseClosed,
    "payment.connected-account.kyb-approved.v1": KybApproved,
    "payment.connected-account.kyb-revoked.v1": KybRevoked,
    "trade.inquiry.created.v1": InquiryCreated,
    "trade.inquiry.declined.v1": InquiryDeclined,
    "trade.inquiry.withdrawn.v1": InquiryWithdrawn,
    "trade.inquiry.expired.v1": InquiryExpired,
    "trade.quote.issued.v1": QuoteIssued,
    "trade.quote.countered.v1": QuoteCountered,
    "trade.quote.accepted.v1": QuoteAccepted,
    "trade.quote.expired.v1": QuoteExpired,
    "trade.schedule-item.due.v1": ScheduleItemDue,
    "trade.order.created.v1": OrderCreated,
    "trade.order.cancelled.v1": OrderCancelled,
    "trade.offline-payment.confirmed.v1": OfflinePaymentConfirmed,
    "trade.shipment.recorded.v1": ShipmentRecorded,
    "trade.shipment.delivered.v1": ShipmentDelivered,
    "trade.order.receipt-confirmed.v1": ReceiptConfirmed,
    "trade.order.completed.v1": OrderCompleted,
    "trade.change-order.approved.v1": ChangeOrderApproved,
    "payment.item.held.v1": ItemHeld,
    "payment.item.released.v1": ItemReleased,
    "payment.item.refunded.v1": ItemRefunded,
};
//# sourceMappingURL=payloads.js.map